import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { 
  Shield, ArrowLeft, ArrowRight, Download, 
  CheckCircle, AlertCircle, XCircle, FileText 
} from "lucide-react";
import { API } from "@/App";

const TenUResLogo = ({ size = "text-4xl" }) => (
  <span className={`tenures-logo ${size}`}>
    Ten<span className="tenures-ur font-extrabold">UR</span>es
  </span>
);

const QUESTIONS = [
  {
    id: "tenant_deceased",
    question: "Has the original tenant passed away?",
    options: [
      { value: "true", label: "Yes" },
      { value: "false", label: "No, they are still living" }
    ]
  },
  {
    id: "relationship",
    question: "What is your relationship to the tenant?",
    options: [
      { value: "spouse", label: "Spouse / Civil Partner / Cohabiting Partner" },
      { value: "child", label: "Son / Daughter" },
      { value: "other_family", label: "Other Family Member" },
      { value: "other", label: "Non-Family Member" }
    ]
  },
  {
    id: "years_in_residence",
    question: "How many years have you lived at this property?",
    type: "number"
  },
  {
    id: "lived_continuously",
    question: "Have you lived at this property continuously (as your only or principal home)?",
    options: [
      { value: "true", label: "Yes, continuously" },
      { value: "false", label: "No, I've had periods away" }
    ]
  },
  {
    id: "previous_succession",
    question: "Has there already been a succession on this tenancy?",
    options: [
      { value: "true", label: "Yes, there has been a previous succession" },
      { value: "false", label: "No, this would be the first" }
    ]
  }
];

const IndicatorBadge = ({ indicator }) => {
  const styles = {
    green: { bg: "indicator-green", icon: CheckCircle, text: "Strong Case" },
    amber: { bg: "indicator-amber", icon: AlertCircle, text: "Needs Review" },
    red: { bg: "indicator-red", icon: XCircle, text: "Complex Case" }
  };
  
  const style = styles[indicator] || styles.amber;
  const Icon = style.icon;
  
  return (
    <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${style.bg}`}>
      <Icon className="w-5 h-5" />
      <span className="font-medium">{style.text}</span>
    </div>
  );
};

export default function SuccessionModule({ user }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [answers, setAnswers] = useState({
    tenant_deceased: "",
    relationship: "",
    years_in_residence: "",
    lived_continuously: "",
    previous_succession: ""
  });

  const currentQuestion = QUESTIONS[step];

  const handleAnswer = (value) => {
    setAnswers({ ...answers, [currentQuestion.id]: value });
  };

  const nextStep = () => {
    if (!answers[currentQuestion.id]) {
      toast.error("Please answer the question");
      return;
    }
    
    if (step < QUESTIONS.length - 1) {
      setStep(step + 1);
    } else {
      calculateResult();
    }
  };

  const calculateResult = async () => {
    setLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/succession/calculate`, {
        tenant_deceased: answers.tenant_deceased === "true",
        relationship: answers.relationship,
        years_in_residence: parseInt(answers.years_in_residence) || 0,
        lived_continuously: answers.lived_continuously === "true",
        previous_succession: answers.previous_succession === "true"
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true
      });
      
      setResult(response.data);
    } catch (error) {
      toast.error("Failed to calculate. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = async () => {
    setDownloadLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/succession/pdf`, {
        tenant_deceased: answers.tenant_deceased === "true",
        relationship: answers.relationship,
        years_in_residence: parseInt(answers.years_in_residence) || 0,
        lived_continuously: answers.lived_continuously === "true",
        previous_succession: answers.previous_succession === "true"
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `TenURes_Succession_${user?.ur_id || 'Guide'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("PDF downloaded successfully");
    } catch (error) {
      toast.error("Failed to generate PDF. Please try again.");
    } finally {
      setDownloadLoading(false);
    }
  };

  const restart = () => {
    setStep(0);
    setResult(null);
    setAnswers({
      tenant_deceased: "",
      relationship: "",
      years_in_residence: "",
      lived_continuously: "",
      previous_succession: ""
    });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 nav-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <TenUResLogo size="text-2xl" />
          <Button 
            variant="ghost" 
            className="text-[#111827] hover:text-[#05D38A]"
            onClick={() => navigate('/dashboard')}
            data-testid="back-to-dashboard"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Dashboard
          </Button>
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <div className="w-16 h-16 rounded-2xl bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-emerald-600" strokeWidth={1.5} />
            </div>
            <h1 className="text-3xl font-bold text-[#111827] mb-2">
              Succession Assessment
            </h1>
            <p className="text-gray-600">
              Protect your family's right to stay. Answer these questions to understand your position.
            </p>
          </motion.div>

          {/* Result View */}
          {result ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Card className="bg-white border-gray-200 shadow-lg">
                <CardHeader className="text-center">
                  <CardTitle className="text-xl text-[#111827] mb-4">
                    Your Preliminary Assessment
                  </CardTitle>
                  <IndicatorBadge indicator={result.indicator} />
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-[#111827]">{result.message}</p>
                  </div>
                  
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-3">Key Points</h3>
                    <ul className="space-y-2">
                      {result.points?.map((point, index) => (
                        <li key={index} className="flex items-start gap-2 text-[#111827]">
                          <span className="text-[#05D38A] mt-1">•</span>
                          {point}
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row gap-3 pt-4">
                    <Button
                      onClick={downloadPDF}
                      className="flex-1 bg-[#05D38A] hover:bg-[#04B577] text-[#111827] font-semibold glow-mint"
                      disabled={downloadLoading}
                      data-testid="download-pdf-btn"
                    >
                      {downloadLoading ? (
                        <div className="w-5 h-5 border-2 border-[#111827] border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <>
                          <Download className="w-4 h-4 mr-2" />
                          Download Officer Discussion Guide
                        </>
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={restart}
                      className="flex-1 border-gray-300 text-[#111827]"
                      data-testid="restart-btn"
                    >
                      Start Again
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ) : (
            /* Question Flow */
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <Card className="bg-white border-gray-200 shadow-lg">
                <CardHeader>
                  <div className="flex items-center gap-2 text-gray-500 text-sm mb-2">
                    <FileText className="w-4 h-4" />
                    Question {step + 1} of {QUESTIONS.length}
                  </div>
                  <CardTitle className="text-xl text-[#111827]">
                    {currentQuestion.question}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {currentQuestion.type === "number" ? (
                    <div className="space-y-2">
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        placeholder="Enter number of years"
                        className="bg-white border-gray-300 text-[#111827] text-lg h-14"
                        value={answers[currentQuestion.id]}
                        onChange={(e) => handleAnswer(e.target.value)}
                        data-testid="years-input"
                      />
                      <p className="text-gray-500 text-sm">Enter 0 if less than a year</p>
                    </div>
                  ) : (
                    <RadioGroup
                      value={answers[currentQuestion.id]}
                      onValueChange={handleAnswer}
                      className="space-y-3"
                    >
                      {currentQuestion.options.map((option) => (
                        <div
                          key={option.value}
                          className={`flex items-center space-x-3 p-4 rounded-lg border transition-all cursor-pointer ${
                            answers[currentQuestion.id] === option.value
                              ? 'border-[#05D38A] bg-[#05D38A]/5'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                          onClick={() => handleAnswer(option.value)}
                          data-testid={`option-${option.value}`}
                        >
                          <RadioGroupItem 
                            value={option.value} 
                            id={option.value}
                            className="border-gray-300 text-[#05D38A]"
                          />
                          <Label 
                            htmlFor={option.value}
                            className="text-[#111827] cursor-pointer flex-1"
                          >
                            {option.label}
                          </Label>
                        </div>
                      ))}
                    </RadioGroup>
                  )}
                  
                  <div className="flex gap-3 pt-4">
                    {step > 0 && (
                      <Button
                        variant="outline"
                        onClick={() => setStep(step - 1)}
                        className="flex-1 border-gray-300 text-[#111827]"
                        data-testid="back-btn"
                      >
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Back
                      </Button>
                    )}
                    <Button
                      onClick={nextStep}
                      className="flex-1 bg-[#05D38A] hover:bg-[#04B577] text-[#111827] font-semibold glow-mint"
                      disabled={loading}
                      data-testid="next-btn"
                    >
                      {loading ? (
                        <div className="w-5 h-5 border-2 border-[#111827] border-t-transparent rounded-full animate-spin" />
                      ) : step < QUESTIONS.length - 1 ? (
                        <>
                          Continue
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </>
                      ) : (
                        "Get Assessment"
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
              
              {/* Progress bar */}
              <div className="mt-6">
                <div className="progress-bar-track h-2">
                  <div 
                    className="progress-bar-fill h-full"
                    style={{ width: `${((step + 1) / QUESTIONS.length) * 100}%` }}
                  />
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </main>
    </div>
  );
}
